import { Modal, Form, Input, Select, message } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import Label from 'common/components/Label';

import { usePackagesQuery } from '../Packages/queries';

import { useUpgradeTrialMutation } from './queries';

const ERROR_MESSAGES_BY_CODE = () => ({
  '12f539f2-793f-43fb-a54c-e083529e15ae': t`New Event Code is the same as the old Event Code.`,
  '11176cdc-c320-4d48-b24c-3c5828c80530': t`Event Code may only contain alpha-numeric characters, underscores, and dashes.`,
  '5ffd32df-fdf1-4267-acfb-a74313057282': t`Event Code is already in use.`,
  'd5147118-d618-4917-b8f8-427a67232988': t`Cannot use trial prefix in new shortcode.`,
});

const UpgradeModal = ({ trial, onClose }) => {
  const [form] = Form.useForm();
  const upgradeTrialMutation = useUpgradeTrialMutation();
  const { data: packages = [], isLoading: packagesLoading } = usePackagesQuery({
    type: 'event_credit,user_credit',
  });

  const handleSubmit = async values => {
    const payload = {
      eventTrialId: trial.id,
      blueprintId: values.blueprintId,
      newShortcode: values.shortcode,
    };

    await upgradeTrialMutation.mutateAsync(payload, {
      onSuccess: () => {
        form.resetFields();
        onClose(true);
        message.success(t`Trial upgraded successfully`);
      },
      onError: err => {
        const code = err?.errors?.[0]?.code;
        message.error(ERROR_MESSAGES_BY_CODE()[code] ?? t`Error upgrading trial`);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  return (
    <Modal
      title={t`Upgrade Trial`}
      open
      okText={t`Upgrade`}
      okButtonProps={{
        loading: upgradeTrialMutation.isLoading,
        htmlType: 'submit',
        form: 'upgrade-trial-form',
      }}
      onCancel={handleCancel}
      cancelButtonProps={{ disabled: upgradeTrialMutation.isLoading }}
      width={500}
      destroyOnClose
    >
      <Form
        form={form}
        id="upgrade-trial-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        preserve={false}
        initialValues={{
          shortcode: trial.eventShortcode,
        }}
      >
        <Form.Item
          name="shortcode"
          label={<Label>{t`New Event Code`}</Label>}
          rules={[
            { required: true, message: t`Event Code is required` },
            { min: 3, message: t`Event Code must be at least 3 characters` },
            { max: 128, message: t`Event Code cannot exceed 128 characters` },
            {
              pattern: /^[a-zA-Z0-9_-]+$/,
              message: t`Event Code may only contain alpha-numeric characters, underscores, and dashes`,
            },
          ]}
        >
          <Input addonBefore="https://www.eventmobi.com/" placeholder={t`Enter new event code`} />
        </Form.Item>

        <Form.Item
          name="blueprintId"
          label={<Label>{t`Package`}</Label>}
          rules={[{ required: true, message: t`Package is required` }]}
        >
          <Select
            placeholder={t`Select package`}
            loading={packagesLoading}
            options={packages.map(pkg => ({ label: pkg.name, value: pkg.id }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

UpgradeModal.propTypes = {
  trial: PropTypes.shape({
    id: PropTypes.string.isRequired,
    eventShortcode: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UpgradeModal;
